import React, { Dispatch, FC, useCallback, useMemo, useReducer, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Alert, Button, Card, CardBody, CardTitle, Col, Row } from 'reactstrap';
import { FormNodeType, FormSectionModel } from '../../../../shared/kiosk';
import { Option } from '../../../../shared/options';
import {
  Department,
  VolunteerDepartmentModel,
  VolunteerForm,
  VolunteerModel,
  VolunteerUpsertModel,
} from '../../../../shared/volunteer';
import { ActionButtonModal, LoadingError, MessageCard } from '../../../components';
import { FormNodeMarkdown } from '../../../components/formElements/models/FormNodeMarkdown';
import { SimpleLinkedTerms } from '../../../modules/dealer/form/SimpleLinkedTerms';
import { FormActionType } from '../../../modules/form/FormReducer';
import { VolunteerContact } from '../../../modules/volunteer/form/VolunteerContact';
import { NewVolunteerDepartments } from '../../../modules/volunteer/form/VolunteerDepartments';
import { VolunteerOption } from '../../../modules/volunteer/form/VolunteerOption';
import { useTranslation } from '../../../translations';
import { isAccessError, isLogicError, omit, useConfig } from '../../../utils';
import { captureError, LogicError } from '../../../utils/errorHandling';

export interface VolunteerFormUser {
  id: number;
  phone?: string | null;
  email?: string | null;
}

interface VolunteerFormProps {
  readonly form: VolunteerForm;
  readonly initialVolunteer?: VolunteerModel;
  readonly user: VolunteerFormUser;
  readonly callback: () => void;
}

const reducer = (old: VolunteerUpsertModel, action: FormActionType) => {
  switch (action.type) {
    case 'updateVolunteerContact': {
      const result: VolunteerUpsertModel = {
        ...old,
        contactMethods: { ...action.methods },
        contactMethod: action.method,
      };

      return result;
    }

    case 'updateVolunteerOption': {
      const result: VolunteerUpsertModel = {
        ...old,
        options: {
          ...old.options,
          [action.optionId.toString()]: action.value,
        },
      };

      return result;
    }

    case 'updateVolunteerDepartment': {
      const result: VolunteerUpsertModel = {
        ...old,
        ...omit(action, 'type'),
      };

      return result;
    }

    case 'setPolicyChecked': {
      if (action.policy === 'volunteerTerms') {
        return {
          ...old,
          volunteerPolicy: action.checked,
        };
      }

      return old;
    }

    default: {
      throw new Error(
        `Unknown action type ${action.type}. Are you sure it should be called on the Volunteer form?`,
      );
    }
  }
};

export const VolunteerFormComponent: FC<VolunteerFormProps> = ({
  form,
  initialVolunteer,
  user,
  callback,
}) => {
  const { ts } = useTranslation();
  const history = useHistory();
  const config = useConfig();
  const [saving, setSaving] = useState(false);

  const initialState = useMemo<VolunteerUpsertModel>(() => {
    const initAvoid: number[] = [];
    const initExperience: number[] = [];
    const initInterest: number[] = [];

    if (initialVolunteer) {
      for (const department of initialVolunteer.departments) {
        for (const state of department.states) {
          switch (state) {
            case 'avoid': {
              initAvoid.push(department.id);
              break;
            }

            case 'experience': {
              initExperience.push(department.id);
              break;
            }

            case 'interest': {
              initInterest.push(department.id);
              break;
            }

            default: {
              continue;
            }
          }
        }
      }
    }

    return {
      volunteerPolicy: !!initialVolunteer,
      options: initialVolunteer?.options ?? {},
      departmentAvoid: initAvoid,
      departmentExperience: initExperience,
      departmentInterest: initInterest,
      contactMethods: initialVolunteer?.contactMethods ?? {},
      contactMethod: initialVolunteer?.contactMethod,
    };
  }, [initialVolunteer]);

  const deleteApplication = useCallback(() => {
    if (!initialVolunteer) {
      toast.error(ts('no_local_application_to_delete_please_refresh_and_try_again'));
      return;
    }

    api
      .deleteVolunteer(initialVolunteer.id)
      .then(() => {
        toast.success(ts('your_volunteer_application_has_been_deleted'));
        history.push('/');
      })
      .catch((error: Error) => {
        if (!isLogicError(error)) {
          captureError(error);
          return;
        }

        toast.error(ts('volunteer.form.you_are_currently_assigned_to', config.contact.email.hr));
      });
  }, [config.contact.email.hr, history, initialVolunteer, ts]);

  const [volunteer, setVolunteer] = useReducer(reducer, initialState);
  const [requiredCheckboxes, setRequiredCheckboxes] = useState<Record<string, boolean>>({});

  const logRequiredCheckbox = useCallback(
    (id: string, checked: boolean) => {
      setRequiredCheckboxes((old) => ({
        ...old,
        [id]: checked,
      }));
    },
    [setRequiredCheckboxes],
  );

  const checkboxesDone = useMemo(() => {
    if (initialVolunteer !== undefined) {
      return true;
    }

    const requiredCheckboxIds = form.layout.sections.flatMap((section) => {
      return section.nodes
        .filter((t) => t.type === 'simpleLinkedTerms' && t.requireChecked)
        .map((t) => t.id);
    });

    return requiredCheckboxIds.every((id) => requiredCheckboxes[id] === true);
  }, [form.layout.sections, initialVolunteer, requiredCheckboxes]);

  const submitForm = useCallback(async () => {
    if (!checkboxesDone) {
      toast.error(ts('not_all_required_agreements_have_been_checked_or_signed'));
      return;
    }

    setSaving(true);

    try {
      const response: VolunteerUpsertModel = {
        ...volunteer,
        contactMethod: volunteer.contactMethod === '' ? undefined : volunteer.contactMethod,
      };

      await api.updateVolunteer(user.id, response);
      setSaving(false);
      toast.success(ts('your_volunteer_application_has_been_saved'));
      callback();
    } catch (error) {
      setSaving(false);
      captureError(error as Error);
    }
  }, [checkboxesDone, ts, volunteer, user.id, callback]);

  return (
    <Row className="justify-content-center" id="volunteerForm">
      <Col lg={8} xs={12}>
        <Row className="justify-content-center">
          <VolunteerStatus volunteer={initialVolunteer} />
          {form.layout.sections.map((section) => (
            <VolunteerFormSectionRenderer
              departments={form.departments}
              dispatch={setVolunteer}
              key={section.id}
              logRequiredCheckbox={logRequiredCheckbox}
              oldVolunteer={initialVolunteer}
              options={form.options}
              section={section}
              state={volunteer}
              user={user}
            />
          ))}
          <Col className="mt-10" lg={6} xs={12}>
            <Button
              block
              color="primary"
              disabled={saving || !checkboxesDone}
              id="submitApplication"
              onClick={submitForm}
            >
              {ts(saving ? 'common.saving' : 'submit')}
            </Button>
          </Col>
          {initialVolunteer && (
            <>
              <Col xs={12} />
              <Col className="mt-10" lg={6} xs={12}>
                <ActionButtonModal
                  actionContent="common.delete"
                  block
                  buttonContent="common.delete_application"
                  color="danger"
                  id="deleteApplication"
                  onComplete={deleteApplication}
                  title="are_you_sure"
                >
                  {ts('your_volunteer_application_ask_delete')}
                </ActionButtonModal>
              </Col>
            </>
          )}
        </Row>
      </Col>
    </Row>
  );
};

interface VolunteerFormSectionRendererProps {
  readonly section: FormSectionModel;
  readonly state: VolunteerUpsertModel;
  readonly options: Option[];
  readonly departments: Department[];
  readonly user: VolunteerFormUser;
  readonly oldVolunteer?: VolunteerModel;
  readonly dispatch: Dispatch<FormActionType>;
  readonly logRequiredCheckbox: (id: string, checked: boolean) => void;
}

const VolunteerFormSectionRenderer: FC<VolunteerFormSectionRendererProps> = ({
  section,
  state,
  options,
  departments,
  oldVolunteer,
  user,
  dispatch,
  logRequiredCheckbox,
}) => {
  if (section.nodes.length === 0) {
    return null;
  }

  return (
    <Col className="mb-10" lg={section.size === 'large' ? 12 : 6} xs={12}>
      <Card>
        <CardBody>
          <CardTitle>{section.name}</CardTitle>
          <Row>
            {section.nodes.map((node) => (
              <VolunteerFormNodeRenderer
                departments={departments}
                dispatch={dispatch}
                key={node.id}
                logRequiredCheckbox={logRequiredCheckbox}
                node={node}
                oldVolunteer={oldVolunteer}
                options={options}
                state={state}
                user={user}
              />
            ))}
          </Row>
        </CardBody>
      </Card>
    </Col>
  );
};

interface VolunteerFormNodeRendererProps {
  readonly node: FormNodeType;
  readonly state: VolunteerUpsertModel;
  readonly options: Option[];
  readonly departments: Department[];
  readonly user: VolunteerFormUser;
  readonly oldVolunteer?: VolunteerModel;
  readonly dispatch: Dispatch<FormActionType>;
  readonly logRequiredCheckbox: (id: string, checked: boolean) => void;
}

const VolunteerFormNodeRenderer: FC<VolunteerFormNodeRendererProps> = ({
  node,
  state,
  options,
  departments,
  oldVolunteer,
  user,
  dispatch,
  logRequiredCheckbox,
}) => {
  const { ts } = useTranslation();

  const approved = oldVolunteer?.departments.some((t) => t.states.includes('assignment')) ?? false;

  switch (node.type) {
    case 'divider': {
      return (
        <Col xs="12">
          <hr />
        </Col>
      );
    }

    case 'markdown': {
      return <FormNodeMarkdown {...node} />;
    }

    case 'volunteerContact': {
      return <VolunteerContact dispatch={dispatch} node={node} state={state} user={user} />;
    }

    case 'volunteerOption': {
      const option = options.find((opt) => opt.id === node.optionId);

      if (!option) {
        return <pre>{ts('bad_node_type', node.type)}</pre>;
      }

      return (
        <VolunteerOption
          disabled={!!node.applicationOnly && approved}
          dispatch={dispatch}
          option={option}
          required={node.required ?? false}
          state={state}
        />
      );
    }

    case 'volunteerDepartments': {
      return (
        <NewVolunteerDepartments
          departments={departments}
          dispatch={dispatch}
          existingDepartments={oldVolunteer?.departments}
          state={state}
        />
      );
    }

    case 'simpleLinkedTerms': {
      return (
        <SimpleLinkedTerms
          checked={state.volunteerPolicy}
          disabled={!!oldVolunteer}
          dispatch={dispatch}
          logRequiredCheckbox={logRequiredCheckbox}
          node={node}
        />
      );
    }

    default: {
      return <pre> {ts('bad_node_type', node.type)} </pre>;
    }
  }
};

export const VolunteerLoadError: FC<{ readonly error: Error }> = ({ error }) => {
  const { ts } = useTranslation();
  const config = useConfig();
  if (isAccessError(error)) {
    return (
      <MessageCard icon="block" id="volunteerBanned" level="danger">
        {ts('form.you_are_currently_banned_from_vol')}
        <br />
        {ts('volunteer.form.please_contact_configcontactemailhr_if_you', config.contact.email.hr)}
      </MessageCard>
    );
  }

  if (isLogicError(error, LogicError.ParentCannotVolunteer)) {
    return (
      <MessageCard icon="face" id="parentCanNotVolunteer" level="warning">
        {ts('common.no_human_being_can_possibly')}
        <br />
        {ts('volunteer.form.thanks_for_offering_but_your')}
      </MessageCard>
    );
  }

  return <LoadingError errorText={ts('common.failed_to_load')} />;
};

const VolunteerStatus: FC<{ readonly volunteer?: VolunteerModel }> = ({ volunteer }) => {
  const { ts } = useTranslation();
  const { contact } = useConfig();
  if (!volunteer) {
    return null;
  }

  const assignments: VolunteerDepartmentModel[] = volunteer.departments.filter((t) =>
    t.states.includes('assignment'),
  );

  if (assignments.length > 0) {
    return (
      <Col lg={12} xs={12}>
        <Alert color="success" id="volunteer-approved">
          <CardTitle>{ts('common.your_application_has_been_approved')}</CardTitle>
          {ts('volunteer.form.you_have_been_approved_as')}
          <ul className="my-5">
            {assignments.map((dept) => (
              <li key={dept.id}>
                <strong>{dept.name}</strong>
              </li>
            ))}
          </ul>
          {ts('volunteer.form.if_you_are_unhappy_with', contact.email.hr)}
        </Alert>
      </Col>
    );
  }

  return (
    <Col lg={12} xs={12}>
      <Alert color="warning" id="volunteer-pending">
        <CardTitle>{ts('volunteer.form.your_application_is_awaiting_assignment')}</CardTitle>
        {ts('volunteer.form.our_hr_department_will_review')}
      </Alert>
    </Col>
  );
};
